import React, { useState } from 'react';
import { DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShoppingCart, Sparkles, ArrowRight } from 'lucide-react';

interface OnboardingAddProductProps {
  onSubmit: (data: {
    name: string;
    price: number;
    category: string;
    url?: string;
  }) => void;
  onClose: () => void;
}

export const OnboardingAddProduct = ({ onSubmit, onClose }: OnboardingAddProductProps) => {
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('');
  const [url, setUrl] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name || !price) {
      return;
    }

    const numericPrice = parseFloat(price.replace(',', '.'));
    
    onSubmit({
      name,
      price: numericPrice,
      category: category || 'Sonstiges',
      url: url || undefined
    });
  };

  return (
    <>
      <DialogHeader className="text-center pb-6">
        <div className="relative w-20 h-20 mx-auto mb-5">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl rotate-6 opacity-20"></div>
          <div className="relative w-20 h-20 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-xl">
            <ShoppingCart className="w-9 h-9 text-white" />
          </div>
        </div>
        <DialogTitle className="text-3xl font-bold bg-gradient-to-r from-emerald-700 via-emerald-600 to-emerald-700 bg-clip-text text-transparent mb-2">
          Was möchtest du kaufen?
        </DialogTitle>
        <DialogDescription className="text-gray-600 text-base leading-relaxed">
          Starte deine Reise zu bewusstem Konsum – füge dein erstes Produkt hinzu
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-gray-700 font-semibold flex items-center gap-2">
            <span className="text-emerald-600">📦</span>
            Produktname *
          </Label>
          <Input
            id="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z.B. MacBook Pro 14, iPhone 15, Gaming-Stuhl..."
            className="h-12 border-2 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 rounded-xl transition-all"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="price" className="text-gray-700 font-semibold flex items-center gap-2">
            <span className="text-emerald-600">💰</span>
            Preis (€) *
          </Label>
          <Input
            id="price"
            required
            type="number"
            min="0"
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="99.99"
            className="h-12 border-2 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 rounded-xl transition-all"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="category" className="text-gray-700 font-semibold flex items-center gap-2">
            <span className="text-emerald-600">🏷️</span>
            Kategorie
          </Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-12 border-2 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 rounded-xl transition-all">
              <SelectValue placeholder="Kategorie wählen (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Business">
                <div className="flex items-center gap-2">
                  <span>💼</span>
                  <span>Business</span>
                </div>
              </SelectItem>
              <SelectItem value="Nice-to-Have">
                <div className="flex items-center gap-2">
                  <span>🎯</span>
                  <span>Nice-to-Have</span>
                </div>
              </SelectItem>
              <SelectItem value="Persönlich">
                <div className="flex items-center gap-2">
                  <span>🏠</span>
                  <span>Persönlich</span>
                </div>
              </SelectItem>
              <SelectItem value="Sonstiges">
                <div className="flex items-center gap-2">
                  <span>📦</span>
                  <span>Sonstiges</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="url" className="text-gray-700 font-semibold flex items-center gap-2">
            <span className="text-emerald-600">🔗</span>
            Produkt-URL (optional)
          </Label>
          <Input
            id="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://..."
            type="url"
            className="h-12 border-2 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 rounded-xl transition-all"
          />
        </div>

        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-emerald-50 via-emerald-50 to-blue-50 p-5 border-2 border-emerald-100">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-200 rounded-full -mr-16 -mt-16 opacity-20"></div>
          <div className="relative flex items-start gap-4">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-emerald-900 mb-1">30-Tage Bedenkzeit</h4>
              <p className="text-emerald-800 text-sm leading-relaxed">
                Dein Produkt kommt auf die Warteliste. Nach 30 Tagen entscheidest du bewusst, ob du es wirklich brauchst.
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onClose}
            className="flex-1 h-12 border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl font-semibold transition-all"
          >
            Abbrechen
          </Button>
          <Button 
            type="submit"
            className="flex-1 h-12 bg-gradient-to-r from-emerald-500 via-emerald-600 to-emerald-500 hover:from-emerald-600 hover:via-emerald-700 hover:to-emerald-600 text-white shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] rounded-xl font-semibold"
          >
            Weiter zur Registrierung
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </form>
    </>
  );
};
